import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon } from '@persona-id/design-tokens/icons';
import styled from 'styled-components';

import { Control } from 'dashboard/components/FormControlPilledInput/types';
import { useGraphQuerying } from 'dashboard/components/GraphModule/hooks/useGraphQuerying';
import GraphQueryEditor from 'dashboard/components/GraphQueryEditor';
import GraphProgressiveLoadingBox from 'dashboard/components/GraphToolbar/GraphProgressiveLoadingBox';
import { ServerSearchableField } from 'dashboard/models/graph-backend';
import {
  GraphQueryBody,
  LeftConstants,
  Operator,
  QueryConditionalConfig,
} from 'dashboard/models/graph-query';
import { GraphQueryTemplate } from 'dashboard/models/graph-query-template';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { Elevation } from 'design-system/variables/effect';
import { BASE } from 'design-system/variables/spacing';
import { ACCOUNT_DATUM_TYPE, DEFAULT_ACCOUNT_DATUM_TYPE_RENDER_CONFIG } from 'lib/constants/graph';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { captureMessage } from 'lib/reporting';

const QueryBarContainer = styled.div`
  border-right: 1px solid ${Color.CoreSlate100};
  padding: ${Spacing[2]};
`;

const QueryBar = styled.div``;

const QueryBarItems = styled.div`
  display: flex;
  gap: ${Spacing[2]};
`;

const QueryInputButtonContainer = styled.div`
  user-select: none;
  width: 128px;
`;

const GraphQueryEditorVisibility = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
  user-select: none;
`;

const LoadMoreVisibility = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
`;

const FloatOver = styled.div`
  position: absolute;
  z-index: ${Z_INDICES.floatingBar};
  margin-top: ${Spacing[4]};
`;

const LoadMoreFloatOver = styled.div<{ width: number }>`
  position: absolute;
  left: ${({ width }) => `${width - 200 - BASE}px`};
  width: 200px;
  z-index: ${Z_INDICES.floatingBar - 1};
  margin-top: ${Spacing[4]};
`;

const ElevatedGraphQueryEditor = styled(GraphQueryEditor)`
  box-shadow: ${Elevation.Low};
`;

const placeholderFromSearchableFieldLeftValue = (leftValue: string): string => {
  const tokens = leftValue.split(':', 3);
  if (tokens.length === 0) {
    captureMessage(`Invalid left value: ${leftValue}`);
    return '';
  }
  return tokens[tokens.length - 1];
};

interface GraphQueryBarProps {
  clearGraphQueryTemplate: () => void;
  graphQueryEditorVisible: boolean;
  graphQueryEditorToggle: () => void;
  graphQueryTemplate: GraphQueryTemplate | null;
  handleClearGraphQueryAndGraphQueryTemplate: () => void;
  handleLoadGraphQueryTemplate: (graphQueryTemplateId: string) => Promise<void>;
  handleQuery: (query: GraphQueryBody) => void;
  handleSaveGraphQueryTemplate: (
    id: string | null,
    name: string,
    description: string | null,
    payload: GraphQueryBody,
    saveAs: boolean,
  ) => Promise<boolean>;
  loadingGraphEnvironment: boolean;
  nodeLimit: number;
  width: number;
}

const GraphQueryBar = ({
  clearGraphQueryTemplate,
  graphQueryEditorToggle,
  graphQueryEditorVisible,
  graphQueryTemplate,
  handleClearGraphQueryAndGraphQueryTemplate,
  handleLoadGraphQueryTemplate,
  handleQuery,
  handleSaveGraphQueryTemplate,
  loadingGraphEnvironment,
  nodeLimit,
  width,
}: GraphQueryBarProps) => {
  const {
    canLoadMore,
    handleLoadMoreResults,
    queryConfigAccountConnections,
    queryConfigByAccount,
    queryConfigByCluster,
    searchableFields,
  } = useGraphQuerying();

  const showGraphSearchableFieldsInUi = useFeatureFlag(
    'dashboard:show-graph-searchable-fields-in-ui',
  );

  return (
    <QueryBarContainer>
      <QueryBar>
        <QueryBarItems>
          <QueryInputButtonContainer>
            <Button
              id='graph-query-bar__query-button'
              width='full'
              variant='basic'
              active={graphQueryEditorVisible}
              onClick={graphQueryEditorToggle}
              suffixIcon={<CaretDownIcon />}
            >
              Query
            </Button>
          </QueryInputButtonContainer>
        </QueryBarItems>
        <FloatOver
          onWheel={(e) => {
            // prevent scrolling of graph canvas beneath it
            e.stopPropagation();
          }}
        >
          <GraphQueryEditorVisibility visible={!loadingGraphEnvironment && graphQueryEditorVisible}>
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <ElevatedGraphQueryEditor
              accountConnectionConditionalConfig={queryConfigAccountConnections}
              clearGraphQueryTemplate={clearGraphQueryTemplate}
              graphQueryTemplate={graphQueryTemplate}
              handleClearGraphQueryAndGraphQueryTemplate={
                handleClearGraphQueryAndGraphQueryTemplate
              }
              handleQuery={handleQuery}
              handleSaveGraphQueryTemplate={handleSaveGraphQueryTemplate}
              loadingGraphEnvironment={loadingGraphEnvironment}
              handleLoadGraphQueryTemplate={handleLoadGraphQueryTemplate}
              queryConfig={[
                {
                  left: {
                    key: ACCOUNT_DATUM_TYPE.name,
                    display: 'Account ID',
                  },
                  operatorToRight: {
                    [Operator.Equals]: {
                      type: Control.Input,
                      multi: false,
                    },
                  },
                  placeHolder: DEFAULT_ACCOUNT_DATUM_TYPE_RENDER_CONFIG.placeHolder,
                },
                ...(queryConfigByAccount || []).filter(
                  // Workaround for new query editor, as Account ID is now a separate query
                  (conditionalConfig: QueryConditionalConfig) => {
                    return (
                      conditionalConfig.left.key !== ACCOUNT_DATUM_TYPE.name &&
                      conditionalConfig.left.key !== ACCOUNT_DATUM_TYPE.token
                    );
                  },
                ),
                ...((showGraphSearchableFieldsInUi && searchableFields) || []).map(
                  // Search by custom fields.
                  (searchableField: ServerSearchableField) => {
                    return {
                      left: {
                        key: searchableField['left-value'],
                        display: searchableField['display-name'],
                      },
                      operatorToRight: {
                        [Operator.Equals]: {
                          type: Control.Input,
                          multi: false,
                        },
                      },
                      // The left value has the form: 'field:<account type token>:field_key', so we'll
                      // default to the field key for now.
                      placeHolder: placeholderFromSearchableFieldLeftValue(
                        searchableField['left-value'],
                      ),
                    };
                  },
                ),
                {
                  left: {
                    key: LeftConstants.ByClusterCreatedAt,
                    display: 'All Accounts',
                  },
                  operatorToRight: {
                    [Operator.Between]: {
                      type: Control.DateTimeRangePicker,
                      fixTime: true,
                    },
                  },
                },
              ]}
              properties={(queryConfigByAccount || [])
                .map((qc: QueryConditionalConfig) => qc.left.key)
                // Account ID is not considered a property, but we're reusing queryConfigByAccount which treated it as one
                .filter((key: string) => key !== 'account' && key !== 'reference_id')}
              sharedPropertiesConfig={(queryConfigByCluster || []).find(
                // Workaround for new query editor, as created_at is collected separately
                (conditionalConfig: QueryConditionalConfig) => {
                  return conditionalConfig.left.key === 'shared_properties';
                },
              )}
              toggleShowGraphQueryEditor={graphQueryEditorToggle}
            />
          </GraphQueryEditorVisibility>
        </FloatOver>
        <LoadMoreFloatOver width={width}>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <LoadMoreVisibility visible={canLoadMore}>
            <GraphProgressiveLoadingBox
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              handleLoadMoreResults={handleLoadMoreResults}
              nodeLimit={nodeLimit}
            />
          </LoadMoreVisibility>
        </LoadMoreFloatOver>
      </QueryBar>
    </QueryBarContainer>
  );
};

export default GraphQueryBar;
